// TierList — Renders tiers within an Application card (Step 2).
// Supports add, rename, delete with last-tier guard.

import { useState } from 'react';
import { useProjectStore } from '../../store/projectStore';

interface TierListProps {
  applicationId: string;
}

type EditingState =
  | { mode: 'none' }
  | { mode: 'add' }
  | { mode: 'rename'; id: string; name: string };

export function TierList({ applicationId }: TierListProps) {
  const project = useProjectStore(s => s.project);
  const addTier = useProjectStore(s => s.addTier);
  const updateTier = useProjectStore(s => s.updateTier);
  const deleteTier = useProjectStore(s => s.deleteTier);

  const [editing, setEditing] = useState<EditingState>({ mode: 'none' });
  const [newName, setNewName] = useState('');

  if (!project) return null;
  const app = project.applications.find(a => a.id === applicationId);
  if (!app) return null;

  const tiers = [...app.tiers].sort((a, b) => a.sortOrder - b.sortOrder);

  function handleAdd() {
    const trimmed = newName.trim();
    if (!trimmed) return;
    addTier(applicationId, trimmed);
    setNewName('');
    setEditing({ mode: 'none' });
  }

  function handleRename(id: string, name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    updateTier(id, { name: trimmed });
    setEditing({ mode: 'none' });
  }

  function vmCountForTier(tierId: string): number {
    return app!.vms.filter(vm => vm.tierId === tierId).length;
  }

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={labelStyle}>Tiers</span>
        {editing.mode === 'none' && (
          <button
            type="button"
            onClick={() => setEditing({ mode: 'add' })}
            style={addBtnStyle}
          >
            + Add Tier
          </button>
        )}
      </div>

      {/* Add tier inline form */}
      {editing.mode === 'add' && (
        <div style={inlineFormStyle}>
          <input
            type="text"
            placeholder="Tier name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
            style={inputStyle}
            autoFocus
          />
          <button type="button" onClick={handleAdd} style={saveBtnStyle}>Add</button>
          <button type="button" onClick={() => { setEditing({ mode: 'none' }); setNewName(''); }} style={cancelBtnStyle}>Cancel</button>
        </div>
      )}

      {/* Tier list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {tiers.map(tier => (
          <div key={tier.id} style={tierRowStyle}>
            {editing.mode === 'rename' && editing.id === tier.id ? (
              <div style={inlineFormStyle}>
                <input
                  type="text"
                  value={editing.name}
                  onChange={e => setEditing({ mode: 'rename', id: tier.id, name: e.target.value })}
                  onKeyDown={e => { if (e.key === 'Enter') handleRename(tier.id, editing.name); }}
                  style={inputStyle}
                  autoFocus
                />
                <button type="button" onClick={() => handleRename(tier.id, editing.name)} style={saveBtnStyle}>Save</button>
                <button type="button" onClick={() => setEditing({ mode: 'none' })} style={cancelBtnStyle}>Cancel</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                <span style={{ fontSize: '13px', color: '#E8EDF2', fontWeight: 500 }}>{tier.name}</span>
                <span style={badgeStyle}>
                  {vmCountForTier(tier.id)} VM{vmCountForTier(tier.id) !== 1 ? 's' : ''}
                </span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                  <button
                    type="button"
                    title="Rename tier"
                    onClick={() => setEditing({ mode: 'rename', id: tier.id, name: tier.name })}
                    style={iconBtnStyle}
                  >
                    ✎
                  </button>
                  {tiers.length > 1 && (
                    <button
                      type="button"
                      title="Delete tier"
                      onClick={() => deleteTier(tier.id)}
                      style={{ ...iconBtnStyle, color: '#E05555' }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#4C5C68',
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
};

const addBtnStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 500,
  color: '#F5A621',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '2px 4px',
};

const tierRowStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderRadius: '4px',
  backgroundColor: '#0a1929',
  border: '1px solid rgba(76,92,104,0.25)',
};

const badgeStyle: React.CSSProperties = {
  fontSize: '10px',
  color: '#4C5C68',
  backgroundColor: '#132840',
  padding: '1px 6px',
  borderRadius: '3px',
};

const iconBtnStyle: React.CSSProperties = {
  padding: '2px 4px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: '12px',
  color: '#8FA4B5',
};

const inlineFormStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginBottom: '4px',
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  padding: '4px 8px',
  fontSize: '12px',
  backgroundColor: '#0a1929',
  border: '1px solid rgba(76,92,104,0.35)',
  borderRadius: '4px',
  color: '#E8EDF2',
  outline: 'none',
};

const saveBtnStyle: React.CSSProperties = {
  padding: '3px 10px',
  fontSize: '11px',
  fontWeight: 600,
  backgroundColor: 'rgba(245,166,33,0.12)',
  color: '#F5A621',
  border: '1px solid rgba(245,166,33,0.5)',
  borderRadius: '4px',
  cursor: 'pointer',
};

const cancelBtnStyle: React.CSSProperties = {
  padding: '3px 10px',
  fontSize: '11px',
  backgroundColor: 'transparent',
  color: '#8FA4B5',
  border: '1px solid rgba(76,92,104,0.35)',
  borderRadius: '4px',
  cursor: 'pointer',
};
